import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchHealthMetrics,
  fetchStatsTimeseries,
  type MetricsResponse,
} from "./health-metrics-api";

export type TimeRange = "1h" | "24h" | "7d";

const CW_SERIES = [
  "ingest.invocations",
  "ingest.errors",
  "ingest.duration.p50",
  "ingest.duration.p99",
] as const;

const STATS_SERIES = [
  "inbox.namespaces.created",
  "inbox.messages.received",
  "inbox.messages.dkim_fail",
  "inbox.messages.spf_fail",
] as const;

function windowMs(range: TimeRange): number {
  if (range === "1h") return 60 * 60 * 1000;
  if (range === "24h") return 24 * 60 * 60 * 1000;
  return 7 * 24 * 60 * 60 * 1000;
}

function buildWindow(range: TimeRange): { from: string; to: string } {
  const to = Date.now();
  const from = to - windowMs(range);
  return {
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
  };
}

function sumPoints(seriesId: string, response: MetricsResponse | null): number {
  if (!response) return 0;
  const series = response.series.find((s) => s.id === seriesId);
  if (!series) return 0;
  return series.points.reduce((acc, p) => acc + p.value, 0);
}

function deriveErrorRate(cwData: MetricsResponse | null): string | null {
  if (!cwData) return null;
  const invocations = sumPoints("ingest.invocations", cwData);
  const errors = sumPoints("ingest.errors", cwData);

  // No data at all — show EmptyState by returning null
  if (invocations === 0 && errors === 0) return null;

  // Errors with no recorded invocations (unusual) — show em dash rather than
  // infinity or >100%.
  if (invocations === 0) return "—";

  return `${((errors / invocations) * 100).toFixed(1)}%`;
}

export interface UseHealthDashboardResult {
  cwData: MetricsResponse | null;
  statsData: MetricsResponse | null;
  errorRate: string | null;
  isLoading: boolean;
  error: string | null;
  timeRange: TimeRange;
  setTimeRange: (range: TimeRange) => void;
}

export function useHealthDashboard(
  baseUrl: string,
  initialRange: TimeRange = "1h"
): UseHealthDashboardResult {
  const [timeRange, setTimeRangeInternal] = useState<TimeRange>(initialRange);
  const [cwData, setCwData] = useState<MetricsResponse | null>(null);
  const [statsData, setStatsData] = useState<MetricsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);

  const load = useCallback(
    async (range: TimeRange): Promise<void> => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      setError(null);

      const { from, to } = buildWindow(range);

      try {
        const [cw, stats] = await Promise.all([
          fetchHealthMetrics(baseUrl, { from, to, series: [...CW_SERIES] }),
          fetchStatsTimeseries(baseUrl, { from, to, series: [...STATS_SERIES] }),
        ]);
        if (requestId !== requestIdRef.current) return;
        setCwData(cw);
        setStatsData(stats);
      } catch (e) {
        if (requestId !== requestIdRef.current) return;
        setCwData(null);
        setStatsData(null);
        setError(e instanceof Error ? e.message : "Failed to load health metrics");
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    },
    [baseUrl]
  );

  useEffect(() => {
    void load(timeRange);
  }, [load, timeRange]);

  const setTimeRange = useCallback((range: TimeRange): void => {
    setTimeRangeInternal(range);
  }, []);

  return {
    cwData,
    statsData,
    errorRate: deriveErrorRate(cwData),
    isLoading,
    error,
    timeRange,
    setTimeRange,
  };
}
